import React, { useState, useEffect } from 'react';
import { Home } from 'lucide-react';
import sendBumpTestEmail from './bumpTestEmailService';
import PVSPageLayout from './PVSPageLayout';
import FormSuccessModal from './FormSuccessModal';
import { getCurrentUserDisplayName } from '../utils/currentUser';
import { getLondonDateKey } from '../utils/londonDate';

const CHECK_ITEMS = [
  {
    field: 'batteryLevelOk',
    label: 'Gas Detector Battery level ok?',
  },
  {
    field: 'freeFromDamage',
    label: 'Gas Detector free from damage?',
  },
  {
    field: 'inCalibration',
    label: 'Gas Detector In Calibration?',
  },
  {
    field: 'lelIncreasedWithPropane',
    label: 'Gas Detector Visual LEL levels increased when exposed to Propane?',
  },
];

const BumpTestForm = ({ onBack, gateStatus, onCompleted }) => {
  const today = getLondonDateKey();
  const [formData, setFormData] = useState({
    inspectorName: '',
    date: today,
    batteryLevelOk: 'no',
    freeFromDamage: 'no',
    inCalibration: 'no',
    lelIncreasedWithPropane: 'no',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  useEffect(() => {
    const name = getCurrentUserDisplayName();
    if (!name) return;
    setFormData((prev) => (prev.inspectorName ? prev : { ...prev, inspectorName: name }));
  }, []);

  const alreadyCompleted = gateStatus?.completedToday;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitMessage('');

    if (alreadyCompleted) {
      setSubmitMessage('Bump test has already been completed today.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (!formData.inspectorName.trim()) {
      setSubmitMessage('Unable to determine your name from login credentials. Please log in again.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setIsSubmitting(true);

    try {
      await sendBumpTestEmail(formData);
      if (typeof onCompleted === 'function') {
        await onCompleted();
      }
      setFormData({
        inspectorName: getCurrentUserDisplayName(),
        date: getLondonDateKey(),
        batteryLevelOk: 'no',
        freeFromDamage: 'no',
        inCalibration: 'no',
        lelIncreasedWithPropane: 'no',
      });
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error submitting form:', error);
      if (error.message?.includes('already been completed')) {
        setSubmitMessage('Bump test has already been completed today.');
      } else {
        setSubmitMessage('Error submitting form. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  if (alreadyCompleted) {
    return (
      <PVSPageLayout className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="pvs-glass rounded-xl shadow-pvs-glow border border-pvs-accent/30 p-8 text-center">
            <h1 className="text-2xl font-bold text-white mb-3">Bump Test</h1>
            <p className="text-pvs-text-muted mb-2">
              Today&apos;s bump test has already been completed
              {gateStatus?.completedBy ? ` by ${gateStatus.completedBy}` : ''}.
            </p>
            <p className="text-sm text-amber-300 mb-6">
              This form will be available again after midnight.
            </p>
            <button
              type="button"
              onClick={onBack}
              className="inline-flex items-center gap-2 px-4 py-2 border border-pvs-accent/40 rounded-md text-sm font-medium text-pvs-accent hover:bg-pvs-accent/10"
            >
              <Home className="h-4 w-4" />
              Back to Forms
            </button>
          </div>
        </div>
      </PVSPageLayout>
    );
  }

  return (
    <PVSPageLayout className="py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="pvs-glass rounded-xl shadow-pvs-glow border border-pvs-accent/30">
          <div className="border-b border-pvs-border p-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h1 className="text-3xl font-bold text-white">Bump Test</h1>
                <p className="text-xs text-pvs-text-dim mt-1">Ref: PVS-GBT-001 | Version: 1.0</p>
              </div>
              <button
                type="button"
                onClick={onBack}
                className="flex items-center justify-center w-12 h-12 flex-shrink-0 rounded-xl pvs-glass pvs-glass-hover transition-all duration-200"
                title="Return to Forms"
              >
                <Home className="w-6 h-6" stroke="url(#icon-two-tone)" />
              </button>
            </div>
            <p className="text-pvs-text-dim text-center">Daily gas detector bump test</p>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {submitMessage && (
              <div className="p-5 rounded-lg border-2 shadow-md bg-red-900/30 text-red-400 border-red-500/30">
                <p className="text-sm font-medium">{submitMessage}</p>
              </div>
            )}

            <div className="border-b border-pvs-border pb-6">
              <h2 className="text-xl font-semibold text-pvs-accent mb-4">Inspector Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">Inspector</label>
                  <input
                    type="text"
                    name="inspectorName"
                    value={formData.inspectorName}
                    readOnly
                    className="block w-full pvs-input opacity-80 cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">Date</label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    readOnly
                    className="block w-full pvs-input opacity-80 cursor-not-allowed"
                  />
                </div>
              </div>
            </div>

            <div className="border-b border-pvs-border pb-6">
              <h2 className="text-xl font-semibold text-pvs-accent mb-4">Daily Checks</h2>
              <div className="space-y-4">
                {CHECK_ITEMS.map(({ field, label }) => (
                  <div key={field} className="flex items-center justify-between gap-4">
                    <label className="text-sm font-medium text-pvs-text-muted flex-1">{label}</label>
                    <div className="flex items-center space-x-3 flex-shrink-0">
                      <span className={`text-sm font-medium ${formData[field] === 'no' ? 'text-red-400' : 'text-pvs-text-dim'}`}>No</span>
                      <button
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, [field]: prev[field] === 'yes' ? 'no' : 'yes' }))}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-pvs-accent/50 focus:ring-offset-2 ${
                          formData[field] === 'yes' ? 'bg-green-600' : 'bg-red-600'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            formData[field] === 'yes' ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                      <span className={`text-sm font-medium ${formData[field] === 'yes' ? 'text-green-400' : 'text-pvs-text-dim'}`}>Yes</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-between pt-6">
              <button
                type="button"
                onClick={onBack}
                className="flex items-center justify-center gap-2 px-4 py-2 border border-pvs-accent/40 rounded-md shadow-sm text-sm font-medium text-pvs-accent bg-transparent hover:bg-pvs-accent/10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pvs-accent/50"
              >
                <Home className="h-4 w-4" />
                Back to Forms
              </button>

              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-semibold text-pvs-bg bg-pvs-accent hover:bg-pvs-accent-dim focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pvs-accent/50 disabled:opacity-50"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Bump Test'}
              </button>
            </div>
          </form>
        </div>
      </div>

      <FormSuccessModal
        isOpen={showSuccessModal}
        formName="Bump Test"
        onClose={() => {
          setShowSuccessModal(false);
          onBack?.();
        }}
      />
    </PVSPageLayout>
  );
};

export default BumpTestForm;
